import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import clsx from 'clsx';

export const DAYS_OF_WEEK = [
  { label: 'signup.direct.steps.prebook.dayPicker.days.monday', value: 1 },
  { label: 'signup.direct.steps.prebook.dayPicker.days.tuesday', value: 2 },
  { label: 'signup.direct.steps.prebook.dayPicker.days.wednesday', value: 3 },
  { label: 'signup.direct.steps.prebook.dayPicker.days.thursday', value: 4 },
  { label: 'signup.direct.steps.prebook.dayPicker.days.friday', value: 5 },
  { label: 'signup.direct.steps.prebook.dayPicker.days.saturday', value: 6 },
  { label: 'signup.direct.steps.prebook.dayPicker.days.sunday', value: 0 },
] as const;

export type DayOfWeek = typeof DAYS_OF_WEEK[number];

type SecondAppointmentConfig = {
  isSecond: boolean;
  disabledDay?: number;
};

type Props = {
  value?: number;
  settings?: SecondAppointmentConfig;
  labelKey?: string;
  disabled?: boolean;
  disabledDays?: number[];
  onChange: (value: number) => void;
};

export const DayOfWeekPicker: React.FC<Props> = ({ 
  value, 
  settings, 
  labelKey, 
  disabled = false,
  disabledDays = [], 
  onChange 
}) => {
  const { t } = useTranslation();

  const uniqueId = uuid.v4()[0];

  const availableDays = settings ? DAYS_OF_WEEK.filter(day => day.value !== settings.disabledDay) : DAYS_OF_WEEK;

  return (
    <div>
      <div className="mb-[8px]">
        <label className="font-inter font-medium text-[13px] leading-[20px]">
          {t(labelKey ?? 'signup.direct.steps.prebook.dayPicker.defaultLabel')}
        </label>
      </div>

      <div
        className={clsx(
          'grid w-full gap-x-e10 gap-y-e14 grid-cols-2',
          'sm:grid-cols-3',
          'md:grid-cols-4',
          'lg:grid-cols-6',
        )}
      >
        {availableDays.map((day, index) => {
          const isDayDisabled = disabled || disabledDays.includes(day.value);
          return (
            <button
              key={day.value}
              type="button"
              onClick={() => !isDayDisabled && onChange(day.value)}
              disabled={isDayDisabled}
              className={clsx(
                'font-inter font-medium text-[14px] leading-[20px] text-center px-e16 py-e14 rounded-sm',
                'border',
                value === day.value
                  ? 'bg-[#EBD7FB] border-[#53534C]'
                  : ['bg-white border-[#D4D3CB]', !isDayDisabled && 'hover:bg-[#F7F0FE] hover:border-[#53534C]'],
                isDayDisabled && ['cursor-not-allowed opacity-50'],
              )}
              data-testid={`day-of-week-${index}-${uniqueId}`}
            >
              {t(day.label)}
            </button>
          );
        })}
      </div>
    </div>
  );
};
